import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';

const AboutPage: React.FC = () => {
    return (
        <div className="about-page">
            <Navigation />

            <section className="about-hero">
                <div className="about-hero__container">
                    <h1 className="about-hero__title">
                        We don&apos;t tell you <span className="about-hero__accent">who you are.</span> We show you what&apos;s stopping you.
                    </h1>
                    <p className="about-hero__subtitle">
                        DoYourStep is a behavioral profiling system built on 50+ years of Nobel Prize-winning research in cognitive psychology, behavioral economics, and neuroscience. We find the exact pattern that&apos;s sabotaging your goals - and give you a science-backed protocol to break through it.
                    </p>
                </div>
            </section>

            <section className="about-mission">
                <div className="about-mission__container">
                    <div className="about-mission__grid">
                        <div className="about-mission__card">
                            <div className="about-mission__icon">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                                </svg>
                            </div>
                            <h3 className="about-mission__card-title">Your problem has a name.</h3>
                            <p className="about-mission__card-text">
                                Personality tests tell you who you are. We find the specific behavioral pattern that&apos;s keeping you stuck right now - across work, relationships, and personal goals.
                            </p>
                        </div>
                        <div className="about-mission__card">
                            <div className="about-mission__icon">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM10 17l-3.5-3.5 1.41-1.41L10 14.17l5.59-5.59L17 10l-7 7z"/>
                                </svg>
                            </div>
                            <h3 className="about-mission__card-title">Every move is backed by proof.</h3>
                            <p className="about-mission__card-text">
                                Every recommendation is rooted in validated research - from Self-Determination Theory to Implementation Intentions. You&apos;ll know exactly why you&apos;re doing it and why it works for your profile.
                            </p>
                        </div>
                        <div className="about-mission__card">
                            <div className="about-mission__icon">
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                                </svg>
                            </div>
                            <h3 className="about-mission__card-title">Retake it. Watch yourself evolve.</h3>
                            <p className="about-mission__card-text">
                                Your profile changes as you change. Retake every 6–8 weeks. Watch which dimensions improve - and where to focus next. This isn&apos;t a label. It&apos;s a living system.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="about-approach">
                <div className="about-approach__container">
                    <h2 className="about-approach__title">Built on research, not trends</h2>
                    <p className="about-approach__subtitle">
                        DoYourStep synthesizes frameworks from Kahneman, Kegan, Dweck, Ryan &amp; Deci, ACT/CBT, emotion regulation, and flow research - put into a format you can use in 8–10 minutes.
                    </p>
                    <div className="about-approach__stats">
                        <div className="about-approach__stat">
                            <span className="about-approach__stat-number">60</span>
                            <span className="about-approach__stat-label">Diagnostic Questions</span>
                        </div>
                        <div className="about-approach__stat">
                            <span className="about-approach__stat-number">5</span>
                            <span className="about-approach__stat-label">Functional Dimensions</span>
                        </div>
                        <div className="about-approach__stat">
                            <span className="about-approach__stat-number">50+</span>
                            <span className="about-approach__stat-label">Years of Research</span>
                        </div>
                        <div className="about-approach__stat">
                            <span className="about-approach__stat-number">20</span>
                            <span className="about-approach__stat-label">Minutes to Results</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="about-how">
                <div className="about-how__container">
                    <h2 className="about-how__title">How it works</h2>
                    <div className="about-how__steps">
                        <div className="about-how__step">
                            <div className="about-how__step-number">1</div>
                            <div className="about-how__step-content">
                                <h3>Answer 30 behavior-based questions</h3>
                                <p>Not how you wish you behaved. We ask about real choices - after mistakes, under pressure, around other people, and when certainty is missing.</p>
                            </div>
                        </div>
                        <div className="about-how__step">
                            <div className="about-how__step-number">2</div>
                            <div className="about-how__step-content">
                                <h3>Get your functional profile</h3>
                                <p>A precise map across Inner Worth, Own Direction, Focus &amp; Balance, Clear Thinking, and Real Connection - plus your strongest blockers, growth patterns, and pendulum.</p>
                            </div>
                        </div>
                        <div className="about-how__step">
                            <div className="about-how__step-number">3</div>
                            <div className="about-how__step-content">
                                <h3>Follow your personalized protocol</h3>
                                <p>Specific micro-actions matched to your blocker - built on Self-Determination Theory, WOOP, Flow, and Implementation Intentions. Your first action is ready the same day.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="about-cta">
                <div className="about-cta__container">
                    <h2 className="about-cta__title">Stop guessing. Start understanding.</h2>
                    <p className="about-cta__text">
                        8–10 minutes. Instant results. A personalized protocol grounded in behavioral science.
                    </p>
                    <Link to="/quiz/doyourstep-quiz" className="about-cta__button">
                        Do your step &rarr;
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default AboutPage;
